import React, { useEffect } from "react";
import daveHanging10 from "../assets/images/dave_hanging_10.svg";
import {
  redirectToThankYou,
  THANK_YOU_URL,
  THANK_YOU_REDIRECT_DELAY,
} from "../config/thankYou";

interface ConfirmationStepProps {
  leadReference: string | null;
  message: string | null;
}

/**
 * Shown once the lead has been created. This is now an interstitial: it holds
 * for a beat so the customer sees their order landed, then sends the WHOLE
 * page (not just this iframe) to the CRISP thank-you page.
 *
 * The manual link is the safety net — if the parent page is running an old
 * embed script that can't action the redirect, the customer can still get
 * there themselves rather than sitting on a screen that never moves.
 */
const ConfirmationStep: React.FC<ConfirmationStepProps> = ({
  leadReference,
  message,
}) => {
  useEffect(() => {
    const timer = window.setTimeout(
      () => redirectToThankYou(),
      THANK_YOU_REDIRECT_DELAY
    );
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <div className="bg-white rounded-lg shadow-2xl p-8 text-center relative border-2 border-crisp-nbn">
      {/* Dave Celebration Image */}
      <div className="mb-6">
        <img
          src={daveHanging10}
          alt="Success!"
          className="w-64 h-64 mx-auto opacity-100"
        />
      </div>
      <div className="mb-6">
        <div className="w-16 h-16 bg-crisp-nbn rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
          <svg
            className="w-12 h-12 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <h2 className="text-3xl font-bold text-crisp-orange mb-2">
          Thank You!
        </h2>
        <p className="text-gray-700">
          {message || "We've received your order successfully."}
        </p>
      </div>

      {leadReference && (
        <div className="bg-orange-50 border-2 border-crisp-orange rounded-lg p-4 mb-6">
          <p className="text-sm text-gray-700 mb-1">Reference Number</p>
          <p className="text-lg font-bold text-crisp-orange">{leadReference}</p>
        </div>
      )}

      <div className="flex items-center justify-center gap-3 text-sm text-gray-600">
        <svg
          className="w-4 h-4 animate-spin text-crisp-orange"
          fill="none"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          />
        </svg>
        <span>Taking you to the next steps&hellip;</span>
      </div>

      <div className="mt-4 text-sm text-gray-600">
        Not redirected?{" "}
        <a
          href={THANK_YOU_URL}
          target="_top"
          rel="noopener"
          className="text-crisp-orange hover:underline font-medium"
        >
          Continue here
        </a>
      </div>
    </div>
  );
};

export default ConfirmationStep;
